// 외주 명세서(업체 화면) — 임플란트 종류별 단가 한번에 넣기 · 단가표에 저장 (대표 2026-11-02 "단가는 임플란트 종류별로", "한번에 단가등록이 되게")
//   서버(functions/lib/outsource-mf.js statementLineOf)와 같은 규칙: 단가표 줄의 system 이 있으면 그 임플란트 종류에만, 없으면 공통.
//   순수 함수만 — 화면(VendorStatements.js)과 시험(__tests__/mfImplantPrice.test.js)이 같이 쓴다.

export const MAX_PRICE_ROWS = 200;   // firestore.rules vendorPriceLists items 상한

/** 임플란트 종류 비교용 — 대소문자·띄어쓰기·기호 무시(서버 sysKeyOf 와 같음) */
export const sysKeyOf = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9가-힣]+/g, '');

/**
 * 11-02 전에 만든 초안은 줄에 임플란트 종류가 없다 → 받은 의뢰서(outsourceOrders)에서 채운다.
 * 명세서 줄은 outsourceMfOrderIds 와 같은 순서로 만들어졌다(서버 cases.map). 케이스 번호가 다르면 건드리지 않는다.
 */
export function backfillItems(items, orderIds, orders) {
    const byId = {};
    (orders || []).forEach((o) => { if (o && o.id) byId[o.id] = o; });
    return (items || []).map((it, i) => {
        if (it.mfKind) return it;
        const o = byId[(orderIds || [])[i]];
        if (!o || (it.caseNo && o.caseNo && it.caseNo !== o.caseNo)) return it;
        const d = o.detail || {};
        const system = String(d.system || '').trim().slice(0, 40);
        const serviceName = system && !String(it.serviceName || '').includes(system) ? `${it.serviceName || ''} · ${system}` : it.serviceName;
        return { ...it, serviceName, mfKind: o.kind || '', mfItem: String(d.item || d.prosthesis || d.structure || '').trim(), implantSystem: system };
    });
}

/**
 * 11-02 대표 "중복되는 단어 한군데는 없어도" → "CNC 밀링(커스텀 어버트먼트) 이거를 그냥 CNC 밀링으로" — 서버 statementLineOf 와 같은 결과.
 *   「CNC 밀링(커스텀 어버트먼트) - 커스텀 어버트먼트 · X」·「CNC 밀링(커스텀 어버트먼트) · X」 → 「CNC 밀링 · X」, 다른 품목은 「CNC 밀링 - 품목」
 */
export function tidyServiceName(name) {
    return String(name || '').replace(/^CNC 밀링\s*\(커스텀 어버트먼트\)(?: - 커스텀 어버트먼트(?= · |$))?/, 'CNC 밀링');
}

/** 같은 품목·소재·임플란트 종류끼리 묶음 — [{ key, label, system, kind, item, material, idxs, qty, price }] (price = 모두 같으면 그 값, 아니면 '') */
export function groupsOf(items) {
    const m = new Map();
    (items || []).forEach((it, i) => {
        const key = [it.mfKind || '', it.mfItem || it.serviceName || '', it.material || '', sysKeyOf(it.implantSystem)].join('|');
        if (!m.has(key)) m.set(key, { key, label: it.mfItem || it.serviceName || '품목', system: it.implantSystem || '', kind: it.mfKind || '', item: it.mfItem || '', material: it.material || '', idxs: [], qty: 0, prices: new Set() });
        const g = m.get(key);
        g.idxs.push(i); g.qty += Number(it.toothCount) || 0; g.prices.add(Number(it.unitPrice) > 0 ? Number(it.unitPrice) : '');
    });
    return [...m.values()].map(({ prices, ...g }) => ({ ...g, price: prices.size === 1 ? [...prices][0] : '' }));
}

/**
 * 단가표에 넣기 — 같은 종류·품목·소재·임플란트 종류 줄이 있으면 값만 바꾸고, 없으면 끝에 더한다.
 * entries: [{ kind, item, material, system, price }] (kind·item 없는 것은 건너뜀)
 * 반환 { items, added, updated, skipped } — 200줄을 넘으면 넘는 것은 skipped
 */
export function mergePriceList(list, entries) {
    const out = (list || []).map((p) => ({ ...p }));
    let added = 0, updated = 0, skipped = 0;
    const same = (p, e) => p.kind === e.kind && String(p.item || '').trim() === e.item && String(p.material || '').trim() === e.material && sysKeyOf(p.system) === sysKeyOf(e.system);
    (entries || []).forEach((raw) => {
        const e = { kind: raw.kind || '', item: String(raw.item || '').trim().slice(0, 40), material: String(raw.material || '').trim().slice(0, 40), system: String(raw.system || '').trim().slice(0, 40), price: Number(raw.price) };
        if (!e.kind || !e.item || !(e.price > 0)) { skipped += 1; return; }
        const hit = out.find((p) => same(p, e));
        if (hit) { if (Number(hit.price) !== e.price) { hit.price = e.price; updated += 1; } return; }
        if (out.length >= MAX_PRICE_ROWS) { skipped += 1; return; }
        out.push({ kind: e.kind, item: e.item, material: e.material, system: e.system, price: e.price, note: '' });
        added += 1;
    });
    return { items: out, added, updated, skipped };
}

/**
 * 받은·발행된 외주 명세서 보기(기공소 화면 StatementSheet)용 — 11-02 전에 만든 명세서는 줄에 작성일·납기일·임플란트 종류가 없다.
 * 의뢰서(outsourceOrders, 순서 = outsourceMfOrderIds)에서 비어 있는 것만 채운다. 저장하지 않는 화면 표시용.
 */
export function fillMfItemsFromOrders(items, orderIds, orders) {
    const filled = backfillItems(items, orderIds, orders);
    const byId = {};
    (orders || []).forEach((o) => { if (o && o.id) byId[o.id] = o; });
    return filled.map((it, i) => {
        const o = byId[(orderIds || [])[i]];
        const base = { ...it, serviceName: tidyServiceName(it.serviceName) };
        if (!o || (it.caseNo && o.caseNo && it.caseNo !== o.caseNo)) return base;
        return { ...base, createdAt: it.createdAt || o.createdAt || null, dueDate: it.dueDate || o.dueDate || o.shippedAt || null };
    });
}
